import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import axios from '~/data/http/axios'
import { setHasBanner } from '~/data/actions/siteActions'
import i18n from '~/i18n'
import {
    captureMailchimpImportCompleted,
    captureMailchimpImportFailed,
    captureMailchimpImportInteracted
} from '~/utils/mailchimpImportAnalytics'
import Mailchimp2Icons from '../../company/integrations/apps/mailchimp2/icons'
import { MAILCHIMP_FLOW_PATH, IMPORT_PROGRESS_PATH } from '~/pages/cp/import/paths'
import { openMailchimpFlow } from '~/pages/cp/import/mailchimpFlowOrigin'

const SESSION_HIDE = 'mailchimp2ImportBarHidden'
const RUNNING_POLL_MS = 2000

export default function Mailchimp2ImportBar() {
    const hasBanner = useSelector((state) => state.siteMaster.hasBanner)
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const [job, setJob] = useState(null)
    const [hidden, setHidden] = useState(() => {
        try {
            return window.sessionStorage.getItem(SESSION_HIDE) === '1'
        } catch (err) {
            return false
        }
    })
    const timer = useRef(null)
    const prevStatus = useRef(null)
    const wasOverlayOpen = useRef(false)

    const overlayOpen = location.pathname.indexOf(MAILCHIMP_FLOW_PATH) === 0

    const clearPollTimer = () => {
        if (timer.current) {
            clearInterval(timer.current)
            timer.current = null
        }
    }

    const syncPolling = (nextJob) => {
        clearPollTimer()
        if (nextJob && nextJob.status === 'running') {
            timer.current = setInterval(poll, RUNNING_POLL_MS)
        }
    }

    const poll = async () => {
        try {
            const res = await axios.get('/application/mailchimp/migrate')
            const nextJob = res.data.job || null
            const nextConnected = !!res.data.connected
            setJob(nextJob)
            syncPolling(nextJob)
            return { job: nextJob, connected: nextConnected }
        } catch (err) {
            setJob(null)
            clearPollTimer()
            return { job: null, connected: false }
        }
    }

    useEffect(() => {
        let cancelled = false
        poll().then(({ job: nextJob, connected: nextConnected }) => {
            if (cancelled) return
            if (!nextJob && !nextConnected) {
                clearPollTimer()
            }
        })
        return () => {
            cancelled = true
            clearPollTimer()
        }
    }, [])

    // Refresh once when leaving the import overlay (not on every route change).
    useEffect(() => {
        if (wasOverlayOpen.current && !overlayOpen) {
            poll()
        }
        wasOverlayOpen.current = overlayOpen
    }, [overlayOpen])

    useEffect(() => {
        const status = job && job.status
        const active = status === 'running' || status === 'error'
        const wasActive = prevStatus.current === 'running' || prevStatus.current === 'error'
        if (active && !wasActive) {
            setHidden(false)
            try {
                window.sessionStorage.removeItem(SESSION_HIDE)
            } catch (err) {}
        }
        // Only on a live running → terminal transition, so stale jobs loaded on app start are not re-counted
        if (prevStatus.current === 'running') {
            if (status === 'complete') {
                captureMailchimpImportCompleted(job)
            } else if (status === 'error') {
                captureMailchimpImportFailed(job)
            }
        }
        prevStatus.current = status
    }, [job])

    const running = job && (job.status === 'running' || job.status === 'error')
    const complete = job && job.status === 'complete' && !job.dismissedComplete
    const visible = !overlayOpen && !hidden && (running || complete)

    // Match Banner: notify layout consumers (inbox/settings) when this strip takes space.
    // Re-assert if another banner clears hasBanner while we are still visible.
    useEffect(() => {
        if (!visible) return undefined
        dispatch(setHasBanner(true))
        return () => {
            dispatch(setHasBanner(false))
        }
    }, [visible, dispatch])

    useEffect(() => {
        if (visible && !hasBanner) {
            dispatch(setHasBanner(true))
        }
    }, [visible, hasBanner, dispatch])

    if (!visible) {
        return null
    }

    const go = async () => {
        captureMailchimpImportInteracted(complete ? 'view' : 'view_progress')
        if (complete) {
            try {
                await axios.post('/application/mailchimp/migrate/dismiss')
            } catch (err) {}
        }
        openMailchimpFlow(history, complete ? MAILCHIMP_FLOW_PATH : IMPORT_PROGRESS_PATH)
    }

    const dismiss = async () => {
        setHidden(true)
        try {
            window.sessionStorage.setItem(SESSION_HIDE, '1')
        } catch (err) {}
        if (complete) {
            try {
                await axios.post('/application/mailchimp/migrate/dismiss')
            } catch (err) {}
        }
    }

    return (
        // `open` keeps legacy fixed-bar CSS (top: 0) from clipping content if that build is still served.
        <div className='nav-banner mailchimp2-import-bar open'>
            <div className='mailchimp2-import-bar__inner'>
                <Mailchimp2Icons size='sm' />
                <div className='mailchimp2-import-bar__copy'>
                    {complete
                        ? i18n.t('mailchimp2:bar.complete')
                        : i18n.t('mailchimp2:bar.progress', { percent: job.percentComplete || 0 })}
                </div>
                {!complete && (
                    <div className='mailchimp2-import-bar__track'>
                        <div
                            className='mailchimp2-import-bar__fill'
                            style={{ width: (job.percentComplete || 0) + '%' }}
                        />
                    </div>
                )}
                {!complete && job.stageLabel ? (
                    <div className='mailchimp2-import-bar__stage'>{job.stageLabel}</div>
                ) : null}
                <div className='mailchimp2-import-bar__actions'>
                    <button type='button' className='button button-sml' onClick={go}>
                        {complete ? i18n.t('mailchimp2:bar.see') : i18n.t('mailchimp2:bar.view')}
                    </button>
                    <button type='button' className='button button-sml grey' onClick={dismiss}>
                        {i18n.t('mailchimp2:bar.dismiss')}
                    </button>
                </div>
            </div>
        </div>
    )
}
